import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../context/auth-context.jsx';
import { adminApi } from '../../../lib/admin-api/index.js';
import {
  buildEmptyMarketingEmailCampaignForm,
  buildMarketingEmailCampaignPayloadFromForm,
  mapMarketingEmailCampaignResponseToDto,
  mapMarketingEmailPreviewResponseToDto,
  validateMarketingEmailCampaignPayload,
} from '../dto/marketing-email-campaigns.dto.js';
import { buildMarketingEmailCampaignRows } from '../model/marketing-email-campaigns.model.js';

function filtersToForm(campaign, fallbackForm = buildEmptyMarketingEmailCampaignForm()) {
  const filters = campaign?.audienceFilters || {};
  const fallbackFilters = fallbackForm || buildEmptyMarketingEmailCampaignForm();

  return {
    name: campaign?.name || '',
    subject: campaign?.subject || '',
    htmlBody: campaign?.htmlBody || fallbackFilters.htmlBody || '',
    textBody: campaign?.textBody || fallbackFilters.textBody || '',
    countries: Array.isArray(filters.countries) ? filters.countries : fallbackFilters.countries || [],
    profileStatuses: Array.isArray(filters.profileStatuses) ? filters.profileStatuses : fallbackFilters.profileStatuses || [],
    languages: Array.isArray(filters.languages) ? filters.languages : fallbackFilters.languages || [],
  };
}

function normalizeList(value) {
  if (Array.isArray(value)) {
    return value.map((item) => String(item || '').trim()).filter(Boolean);
  }

  return String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

function mapCountryOptions(payload) {
  const raw = payload?.countries ?? payload?.items ?? payload?.data ?? payload;
  const list = Array.isArray(raw) ? raw : (raw && typeof raw === 'object' ? Object.values(raw) : []);
  const options = list
    .map((item) => {
      if (typeof item === 'string') {
        const value = item.trim().toUpperCase();
        return value ? { value, label: value } : null;
      }

      if (!item || typeof item !== 'object') {
        return null;
      }

      const value = String(item.code || item.value || item.country || '').trim().toUpperCase();
      if (!value) {
        return null;
      }

      const name = String(item.name || item.label || '').trim();
      return { value, label: name ? `${value} - ${name}` : value };
    })
    .filter(Boolean);

  const uniq = new Map();
  options.forEach((option) => uniq.set(option.value, option));
  return Array.from(uniq.values()).sort((left, right) => left.value.localeCompare(right.value));
}

export function useMarketingEmailCampaignEditor(mode = 'edit') {
  const { token, hasPermission } = useAuth();
  const { uid } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [form, setForm] = useState(buildEmptyMarketingEmailCampaignForm());
  const [countryOptions, setCountryOptions] = useState([]);
  const [preview, setPreview] = useState(null);
  const [isLoading, setIsLoading] = useState(mode === 'edit');
  const [isSaving, setIsSaving] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formErrors, setFormErrors] = useState({});

  const loadCampaign = useCallback(async () => {
    if (mode !== 'edit' || !uid) {
      setCampaign(null);
      setForm(buildEmptyMarketingEmailCampaignForm());
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess('');
    setFormErrors({});

    try {
      const response = await adminApi.getMarketingEmailCampaign(token, uid);
      const nextCampaign = mapMarketingEmailCampaignResponseToDto(response);
      setCampaign(nextCampaign);
      setForm(filtersToForm(nextCampaign));
    } catch (requestError) {
      setError(requestError.message || 'Failed to load campaign');
    } finally {
      setIsLoading(false);
    }
  }, [mode, token, uid]);

  useEffect(() => {
    loadCampaign();
  }, [loadCampaign]);

  useEffect(() => {
    let isCancelled = false;

    adminApi.getCountryDictionary(token)
      .then((response) => {
        if (!isCancelled) {
          setCountryOptions(mapCountryOptions(response));
        }
      })
      .catch(() => {
        if (!isCancelled) {
          setCountryOptions([]);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [token]);

  const onFormChange = useCallback((key, value) => {
    const nextValue = ['countries', 'profileStatuses', 'languages'].includes(key) ? normalizeList(value) : value;
    setForm((prev) => ({ ...prev, [key]: nextValue }));
    setFormErrors((prev) => {
      if (!prev[key]) {
        return prev;
      }

      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const saveCampaign = useCallback(async (event) => {
    event?.preventDefault?.();

    const payload = buildMarketingEmailCampaignPayloadFromForm(form);
    const nextErrors = validateMarketingEmailCampaignPayload(payload);
    if (Object.keys(nextErrors).length > 0) {
      setFormErrors(nextErrors);
      setError('Please fix validation errors.');
      setSuccess('');
      return null;
    }

    setIsSaving(true);
    setError('');
    setSuccess('');
    setFormErrors({});

    try {
      const response = campaign?.uid
        ? await adminApi.updateMarketingEmailCampaign(token, campaign.uid, payload)
        : await adminApi.createMarketingEmailCampaign(token, payload);
      const nextCampaign = mapMarketingEmailCampaignResponseToDto(response);
      setCampaign(nextCampaign);
      setForm(filtersToForm(nextCampaign, {
        ...form,
        htmlBody: payload.htmlBody,
        textBody: payload.textBody || '',
        countries: payload.audienceFilters.countries,
        profileStatuses: payload.audienceFilters.profileStatuses,
        languages: payload.audienceFilters.languages,
      }));
      setSuccess('Campaign saved.');
      if (!campaign?.uid && nextCampaign.uid) {
        navigate(`/marketing/emails/campaigns/${encodeURIComponent(nextCampaign.uid)}`, { replace: true });
      }
      return nextCampaign;
    } catch (requestError) {
      setError(requestError.message || 'Failed to save campaign');
      return null;
    } finally {
      setIsSaving(false);
    }
  }, [campaign, form, navigate, token]);

  const loadPreview = useCallback(async () => {
    if (!campaign?.uid) {
      setError('Save the campaign before previewing it.');
      return;
    }

    setIsPreviewing(true);
    setError('');
    setSuccess('');

    try {
      const payload = buildMarketingEmailCampaignPayloadFromForm(form);
      const response = await adminApi.previewMarketingEmailCampaign(token, campaign.uid, payload);
      setPreview(mapMarketingEmailPreviewResponseToDto(response));
    } catch (requestError) {
      setError(requestError.message || 'Failed to render preview');
    } finally {
      setIsPreviewing(false);
    }
  }, [campaign, form, token]);

  const sendCampaign = useCallback(async () => {
    if (!campaign?.uid) {
      return;
    }

    setIsSending(true);
    setError('');
    setSuccess('');

    try {
      const response = await adminApi.sendMarketingEmailCampaign(token, campaign.uid);
      const nextCampaign = mapMarketingEmailCampaignResponseToDto(response);
      setCampaign(nextCampaign);
      setSuccess('Campaign queued for sending.');
    } catch (requestError) {
      setError(requestError.message || 'Failed to send campaign');
    } finally {
      setIsSending(false);
    }
  }, [campaign, token]);

  const campaignRow = useMemo(() => buildMarketingEmailCampaignRows(campaign ? [campaign] : [])[0] || null, [campaign]);
  const canSend = Boolean(campaignRow?.canSend && hasPermission('marketing_email.send'));

  return {
    campaign,
    campaignRow,
    form,
    countryOptions,
    preview,
    isLoading,
    isSaving,
    isPreviewing,
    isSending,
    error,
    success,
    formErrors,
    canSend,
    onFormChange,
    saveCampaign,
    loadPreview,
    sendCampaign,
  };
}
